import React, { useMemo, useState } from 'react';
import groupBy from 'lodash/groupBy';
import Tooltip from '@atlaskit/tooltip';
import { Y300, Y75 } from '@atlaskit/theme/colors';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import styled from '@emotion/styled';
import DropdownMenu from '@atlaskit/dropdown-menu';
import Form, { FormFooter } from '@atlaskit/form';
import SectionMessage from '@atlaskit/section-message';
import type { ExternalProductsList } from '../../types';
import { OperationType } from '../../../../../types';
import { AddCustomerEmailField } from '../../../../entitlement-transfer/src/components/add-customer-email-field';
import isEmail from 'validator/lib/isEmail';
import { useStore } from '../../../../../store';

const StyledWarningButton = styled(Button)({
  backgroundColor: Y75,
  padding: '0 8px',
  marginRight: '4px',
});

const StyledFormWrapper = styled.div({
  [`[role='menu']`]: {
    minWidth: '400px',
  },
});

const StyledFormContentWrapper = styled.div({
  padding: '16px 24px',
});
/**
 * Since, the dropdown needs to be added inside the sidebar only
 * 1. Need to be fixed position in order to come outside sidebar in terms of z-index for small screens
 * 2. For large, screens no need of fixing the menu
 */
const windowSpecificDropdownProps =
  window.innerWidth < 2000
    ? {
        position: 'bottom right',
        isMenuFixed: true,
        shouldFlip: true,
      }
    : {};

type Props = {
  externalProductsList: ExternalProductsList;
  currentCloudSiteHostname: string;
  availableEntitlementIds: string[];
  operationType?: OperationType;
  isTransfer?: boolean;
  onTransferSearchFormSubmit: (data: any, currentCloudSiteHostname?: string) => Promise<void>;
};

export const ExternalProductsIndicatorContainer = ({
  externalProductsList,
  currentCloudSiteHostname,
  isTransfer,
  availableEntitlementIds,
  onTransferSearchFormSubmit,
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [emailList, setEmailList] = useState<Map<string, { email: string; error?: string | undefined }>>(new Map());
  const [{ sidebarFilter }, actions] = useStore();
  const externalProductListGroupedByCloudSiteId = groupBy(externalProductsList, 'cloudSiteHostname');

  const hasExternalProducts = !!externalProductListGroupedByCloudSiteId[currentCloudSiteHostname]?.length;

  const isTransferSearchSubmitBtnDisabled = useMemo(() => {
    const emailListArray = Array.from(emailList);
    if (!emailListArray.length) return true;

    const invalidEmail = emailListArray.find(([_key, { email }]) => {
      return !email || !isEmail(email);
    });

    return !!invalidEmail;
  }, [emailList]);

  if (!hasExternalProducts) return null;

  // By now we know there are external products. In case of transfer, check if any of them have not been fetched.
  const externalEntitlementsPendingFetch = externalProductListGroupedByCloudSiteId[currentCloudSiteHostname].find(
    ({ id }) => !availableEntitlementIds.includes(id)
  );

  const warningButton = (
    <StyledWarningButton
      iconBefore={<WarningIcon label="" primaryColor={Y300} />}
      testId={`external-product-indicator-${currentCloudSiteHostname}`}
    />
  );

  const onFormSubmit = async (data: any) => {
    /**
     * * Need to pass the currentCloudSiteHostname to the onTransferSearchFormSubmit function
     *
     * When the cloudsite is present in the sidebar, but external products exist on the cloudsite.
     * Under this case, partial search form opens on clicking the warning icon, but search query is not set.
     * So, we need currentCloudSiteHostname to fetch the external products in onTransferSearchFormSubmit function.
     */
    await onTransferSearchFormSubmit(data, currentCloudSiteHostname);
    if (!isTransfer && sidebarFilter?.searchQuery !== currentCloudSiteHostname) {
      actions.setSearchQuery(currentCloudSiteHostname);
    }
    setIsOpen(false);
  };

  // Here, if all external products have been fetched, we should not show the warning icon next to cloudsite
  if (!externalEntitlementsPendingFetch) return null;

  const formContent = (
    <Form onSubmit={onFormSubmit}>
      {({ formProps, submitting }) => (
        <form {...formProps}>
          <StyledFormContentWrapper>
            <SectionMessage appearance="warning" title="Billing admin(s) missing">
              Some subscriptions can't be shown To view them, enter an authorized customer billing admin email.
            </SectionMessage>

            <AddCustomerEmailField className="mt-16" emailList={emailList} setEmailList={setEmailList} />

            <FormFooter>
              <ButtonGroup>
                <Button appearance="default" type="button" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>

                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={submitting}
                  testId="submit-transfer-form"
                  isDisabled={isTransferSearchSubmitBtnDisabled}
                >
                  Next
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </StyledFormContentWrapper>
        </form>
      )}
    </Form>
  );

  return (
    <StyledFormWrapper>
      <DropdownMenu
        isOpen={isOpen}
        trigger={
          <Button
            testId="entitlement-transfer-menu"
            appearance="subtle"
            iconBefore={warningButton}
            onClick={() => setIsOpen(true)}
          />
        }
        appearance="tall"
        {...windowSpecificDropdownProps}
      >
        {formContent}
      </DropdownMenu>
    </StyledFormWrapper>
  );
};
